import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, pairwise, startWith, switchMap, take, tap } from 'rxjs/operators';
import { first, groupBy, uniq, uniqBy } from 'lodash-es';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { NimbusSignalRHub } from '@nimbus-global/features/signalr/nimbusSignalR.hub';
import { ReportingUserSessionsService } from '@nimbus-global/features/reportingLiveData/reporting-user-sessions.service';
import { IUserSessionLive, UserSessionState } from '@nimbus-global/features/reportingLiveData/models/user-session.model';
import { isUserSessionLiveChatEnabled } from '@nimbus-global/features/chat/utils/chat-enabled.utils';
import { InitialModality } from '@nimbus-library/features/shared/models/participant-info/i-combined-conversation.response';
import { MySessionsNotificationApiClient } from './api/my-sessions-notification.api-client';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class MySessionsNotificationService {
  private selectedInteractionConversationId: string | null = null;

  public constructor(
    private readonly reportingUserSessionsService: ReportingUserSessionsService,
    private readonly nimbusSignalRHub: NimbusSignalRHub,
    private readonly authenticationService: IAuthenticationService,
    private readonly apiClient: MySessionsNotificationApiClient,
  ) {}

  public initialize(): void {
    this.authenticationService.isAuthenticated$
      .pipe(
        distinctUntilChanged(),
        filter((isAuthenticated) => isAuthenticated),
        switchMap(() => this.nimbusSignalRHub.onChatHandlerMessageAdded$),
        filter((message) => !!message?.conversationId),
        filter((message) => message.conversationId !== this.selectedInteractionConversationId),
        tap((message) => this.setUserSessionLiveHasNewMessages(message.conversationId, true)),
        untilDestroyed(this),
      )
      .subscribe();

    this.reportingUserSessionsService
      .getUserSessionsLive$()
      .pipe(
        startWith([] as IUserSessionLive[]),
        pairwise(),
        tap(([prev, curr]) => {
          const prevById = groupBy(prev, (x) => x.id);
          curr.forEach((item) => {
            const previousItem = first(prevById[item.id]);
            if (
              previousItem &&
              previousItem.state === UserSessionState.Selected &&
              isUserSessionLiveChatEnabled(item.channel, item.state) &&
              item.conversationId !== this.selectedInteractionConversationId
            ) {
              this.setUserSessionLiveHasNewMessages(item.conversationId, true);
            }
          });
        }),
        untilDestroyed(this),
      )
      .subscribe();

    this.enrichImUnreadStateOnSync();
  }

  public setSelectedInteractionConversationId(conversationId: string | null): void {
    this.selectedInteractionConversationId = conversationId;

    if (conversationId) {
      this.setUserSessionLiveHasNewMessages(conversationId, false);
    }
  }

  public getUserSessionsLiveWithNewMessagesIncoming$(): Observable<IUserSessionLive[]> {
    return this.reportingUserSessionsService
      .getUserSessionsLive$()
      .pipe(map((items) => items.filter((x) => isUserSessionLiveChatEnabled(x.channel, x.state) && x.imHasNewMessages)));
  }

  public getUserMySessionsMenuIndicatorCount$(): Observable<number> {
    return combineLatest([
      this.reportingUserSessionsService.getUserSessionsLiveIncoming$(),
      this.getUserSessionsLiveWithNewMessagesIncoming$(),
    ]).pipe(
      map(([incoming, unread]) => uniqBy([...incoming, ...unread], 'conversationId').length),
      distinctUntilChanged(),
    );
  }

  private setUserSessionLiveHasNewMessages(conversationId: string, imHasNewMessages: boolean): void {
    const sessions = this.reportingUserSessionsService.getUserSessionsLive();
    const sessionIndex = sessions.findIndex((session) => session.conversationId === conversationId);

    if (sessionIndex < 0) {
      return;
    }

    const updatedSession: IUserSessionLive = {
      ...sessions[sessionIndex],
      imHasNewMessages,
    };

    this.reportingUserSessionsService.setUserSessionsLive([
      ...sessions.slice(0, sessionIndex),
      updatedSession,
      ...sessions.slice(sessionIndex + 1),
    ]);
  }

  private enrichImUnreadStateOnSync(): void {
    this.reportingUserSessionsService.initialized$
      .pipe(
        switchMap(() => {
          const userInfo = this.authenticationService.getUserInfo();
          const sessions = this.reportingUserSessionsService.getUserSessionsLive();
          const conversationIds = uniq(
            sessions
              .filter(
                (x) =>
                  x.initialModality === InitialModality.InstantMessaging &&
                  isUserSessionLiveChatEnabled(x.channel, x.state) &&
                  !!x.conversationId,
              )
              .map((x) => x.conversationId),
          );

          if (!userInfo?.tenantId || !conversationIds.length) {
            return of({} as Record<string, boolean>);
          }

          return this.apiClient
            .getImHasNewMessages(userInfo.tenantId, userInfo.userId, conversationIds)
            .pipe(catchError(() => of({} as Record<string, boolean>)));
        }),
        tap((data) => {
          Object.entries(data).forEach(([conversationId, hasNewMessages]) => {
            if (hasNewMessages) {
              this.setUserSessionLiveHasNewMessages(conversationId, true);
            }
          });
        }),
        take(1),
        untilDestroyed(this),
      )
      .subscribe();
  }
}
